/**
 * DLQ Monitoring Dashboard
 *
 * Admin-facing page for monitoring and managing dead letter queue entries.
 * Features: stats banner, filter bar, paginated entry list, entry detail modal.
 *
 * Story: SCRUM-564 - DLQ Monitoring Dashboard
 * MVP: 1.5.2 - Booking Payment Domain Model
 *
 * API Endpoints:
 * - GET /api/v1/admin/dlq           - List entries (paginated, filterable)
 * - GET /api/v1/admin/dlq/stats     - Get aggregate stats
 * - GET /api/v1/admin/dlq/:id       - Get entry detail
 * - POST /api/v1/admin/dlq/:id/retry    - Retry an entry
 * - POST /api/v1/admin/dlq/:id/resolve  - Resolve an entry
 * - POST /api/v1/admin/dlq/:id/dismiss  - Dismiss an entry
 * - POST /api/v1/admin/dlq/:id/escalate - Escalate an entry
 */

import React, { useState, useEffect, useCallback } from 'react';

const STATUS_BADGES = {
  pending: { label: 'Pending', color: '#f59e0b', bg: '#fef3c7' },
  retrying: { label: 'Retrying', color: '#3b82f6', bg: '#dbeafe' },
  resolved: { label: 'Resolved', color: '#10b981', bg: '#d1fae5' },
  dismissed: { label: 'Dismissed', color: '#6b7280', bg: '#f3f4f6' },
  escalated: { label: 'Escalated', color: '#ef4444', bg: '#fee2e2' }
};

const QUEUE_COLORS = {
  'payment.process': '#8b5cf6',
  'payment.refund': '#f59e0b',
  'payment.payout': '#10b981',
  'payment.ledger': '#3b82f6',
  'payment.notification': '#6b7280'
};

function getQueueColor(queueName) {
  return Object.prototype.hasOwnProperty.call(QUEUE_COLORS, queueName)
    ? QUEUE_COLORS[queueName]
    : '#6b7280';
}

function getStatusBadge(status) {
  return Object.prototype.hasOwnProperty.call(STATUS_BADGES, status)
    ? STATUS_BADGES[status]
    : { label: status, color: '#6b7280', bg: '#f3f4f6' };
}

function formatRelativeTime(dateStr) {
  if (!dateStr) {
    return 'N/A';
  }
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) {
    return 'just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  return `${diffDays}d ago`;
}

function truncate(str, maxLen = 80) {
  if (!str) {
    return '';
  }
  return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
}

// ─── Stats Banner ────────────────────────────────────────────────────

function StatsBanner({ stats }) {
  if (!stats) {
    return null;
  }

  return (
    <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
      <StatCard label="Pending" value={stats.pending || 0} color="#f59e0b" />
      <StatCard label="Retrying" value={stats.retrying || 0} color="#3b82f6" />
      <StatCard label="Escalated" value={stats.escalated || 0} color="#ef4444" />
      <StatCard label="Resolved" value={stats.resolved || 0} color="#10b981" />
      {stats.oldestPendingAt && (
        <StatCard label="Oldest Pending" value={formatRelativeTime(stats.oldestPendingAt)} color="#6b7280" />
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      padding: '16px',
      border: `2px solid ${color}`,
      borderRadius: '8px',
      minWidth: '120px',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '24px', fontWeight: 'bold', color }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#6b7280' }}>{label}</div>
    </div>
  );
}

// ─── Filter Bar ──────────────────────────────────────────────────────

function FilterBar({ filters, onFilterChange }) {
  return (
    <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
      <select
        value={filters.status || ''}
        onChange={(e) => onFilterChange({ ...filters, status: e.target.value || undefined })}
        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
      >
        <option value="">All Statuses</option>
        <option value="pending">Pending</option>
        <option value="retrying">Retrying</option>
        <option value="resolved">Resolved</option>
        <option value="dismissed">Dismissed</option>
        <option value="escalated">Escalated</option>
      </select>

      <select
        value={filters.original_queue || ''}
        onChange={(e) => onFilterChange({ ...filters, original_queue: e.target.value || undefined })}
        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
      >
        <option value="">All Queues</option>
        <option value="payment.process">payment.process</option>
        <option value="payment.refund">payment.refund</option>
        <option value="payment.payout">payment.payout</option>
        <option value="payment.ledger">payment.ledger</option>
        <option value="payment.notification">payment.notification</option>
      </select>
    </div>
  );
}

// ─── Entry List ──────────────────────────────────────────────────────

function EntryList({ entries, pagination, onPageChange, onAction, onSelect }) {
  if (!entries || entries.length === 0) {
    return <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>No DLQ entries found.</div>;
  }

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
            <th style={{ padding: '8px' }}>Queue</th>
            <th style={{ padding: '8px' }}>Event Type</th>
            <th style={{ padding: '8px' }}>Error</th>
            <th style={{ padding: '8px' }}>Retries</th>
            <th style={{ padding: '8px' }}>Failed</th>
            <th style={{ padding: '8px' }}>Status</th>
            <th style={{ padding: '8px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr
              key={entry.id}
              style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
              onClick={() => onSelect(entry)}
            >
              <td style={{ padding: '8px' }}>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  backgroundColor: getQueueColor(entry.original_queue),
                  color: 'white'
                }}>
                  {entry.original_queue}
                </span>
              </td>
              <td style={{ padding: '8px', fontSize: '13px' }}>{entry.event_type}</td>
              <td style={{ padding: '8px', fontSize: '13px', color: '#6b7280' }}>{truncate(entry.error_message)}</td>
              <td style={{ padding: '8px', fontSize: '13px' }}>{entry.retry_count}/{entry.max_retries}</td>
              <td style={{ padding: '8px', fontSize: '13px', color: '#6b7280' }}>{formatRelativeTime(entry.failed_at)}</td>
              <td style={{ padding: '8px' }}>
                <StatusBadge status={entry.status} />
              </td>
              <td style={{ padding: '8px' }}>
                <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                  {entry.status === 'pending' && (
                    <>
                      <ActionButton label="Retry" onClick={() => onAction(entry.id, 'retry')} color="#3b82f6" />
                      <ActionButton label="Resolve" onClick={() => onAction(entry.id, 'resolve')} color="#10b981" />
                      <ActionButton label="Dismiss" onClick={() => onAction(entry.id, 'dismiss')} color="#6b7280" />
                      <ActionButton label="Escalate" onClick={() => onAction(entry.id, 'escalate')} color="#ef4444" />
                    </>
                  )}
                  {entry.status === 'escalated' && (
                    <>
                      <ActionButton label="Retry" onClick={() => onAction(entry.id, 'retry-escalated')} color="#f59e0b" />
                      <ActionButton label="Resolve" onClick={() => onAction(entry.id, 'resolve')} color="#10b981" />
                      <ActionButton label="Dismiss" onClick={() => onAction(entry.id, 'dismiss')} color="#6b7280" />
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pagination && pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '16px' }}>
          <button
            disabled={pagination.page <= 1}
            onClick={() => onPageChange(pagination.page - 1)}
            style={{ padding: '4px 12px' }}
          >
            Prev
          </button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => onPageChange(pagination.page + 1)}
            style={{ padding: '4px 12px' }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const badge = getStatusBadge(status);
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      backgroundColor: badge.bg,
      color: badge.color,
      fontWeight: 600
    }}>
      {badge.label}
    </span>
  );
}

function ActionButton({ label, onClick, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '2px 8px',
        fontSize: '11px',
        border: `1px solid ${color}`,
        borderRadius: '4px',
        color,
        backgroundColor: 'transparent',
        cursor: 'pointer'
      }}
    >
      {label}
    </button>
  );
}

// ─── Entry Detail Modal ──────────────────────────────────────────────

function EntryDetailModal({ entry, onClose }) {
  if (!entry) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '700px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>DLQ Entry Detail</h2>
          <button onClick={onClose} style={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: '20px' }}>x</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div><strong>ID:</strong> <code>{entry.id}</code></div>
          <div><strong>Status:</strong> <StatusBadge status={entry.status} /></div>
          <div><strong>Queue:</strong> {entry.original_queue}</div>
          <div><strong>Event Type:</strong> {entry.event_type}</div>
          <div><strong>Retries:</strong> {entry.retry_count}/{entry.max_retries}</div>
          <div><strong>Failed:</strong> {formatRelativeTime(entry.failed_at)}</div>
          {entry.original_job_id && <div><strong>Job ID:</strong> <code>{entry.original_job_id}</code></div>}
          {entry.error_code && <div><strong>Error Code:</strong> {entry.error_code}</div>}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <strong>Error Message:</strong>
          <pre style={{ backgroundColor: '#f3f4f6', padding: '8px', borderRadius: '4px', overflow: 'auto', fontSize: '13px' }}>
            {entry.error_message}
          </pre>
        </div>

        {entry.error_stack && (
          <div style={{ marginBottom: '12px' }}>
            <strong>Stack Trace:</strong>
            <pre style={{ backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px', overflow: 'auto', fontSize: '11px', maxHeight: '200px' }}>
              {entry.error_stack}
            </pre>
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          <strong>Payload:</strong>
          <pre style={{ backgroundColor: '#f0fdf4', padding: '8px', borderRadius: '4px', overflow: 'auto', fontSize: '12px', maxHeight: '300px' }}>
            {JSON.stringify(entry.payload, null, 2)}
          </pre>
        </div>

        {entry.resolution_notes && (
          <div>
            <strong>Resolution Notes:</strong>
            <p>{entry.resolution_notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Notes Input Modal ──────────────────────────────────────────────

function NotesModal({ action, onConfirm, onCancel }) {
  const [notes, setNotes] = useState('');

  const isEscalatedRetry = action === 'retry-escalated';

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '400px',
        width: '90%'
      }}>
        {isEscalatedRetry ? (
          <>
            <h3 style={{ margin: '0 0 8px', color: '#f59e0b' }}>Retry Escalated Entry</h3>
            <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#6b7280' }}>
              This entry has exhausted all automatic retries. Retrying will reset the retry counter and attempt processing again. Are you sure?
            </p>
          </>
        ) : (
          <h3 style={{ margin: '0 0 12px' }}>Enter {action} notes</h3>
        )}
        {!isEscalatedRetry && (
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', resize: 'vertical' }}
            placeholder={`Reason for ${action}...`}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
          <button onClick={onCancel} style={{ padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={() => {
              if (isEscalatedRetry) {
                onConfirm('');
              } else if (notes.trim()) {
                onConfirm(notes.trim());
              }
            }}
            disabled={!isEscalatedRetry && !notes.trim()}
            style={{
              padding: '6px 16px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: isEscalatedRetry ? '#f59e0b' : '#3b82f6',
              color: 'white',
              cursor: (isEscalatedRetry || notes.trim()) ? 'pointer' : 'not-allowed'
            }}
          >
            {isEscalatedRetry ? 'Retry' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Error Banner ───────────────────────────────────────────────────

function ErrorBanner({ message, onDismiss }) {
  if (!message) {
    return null;
  }
  return (
    <div style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '4px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{message}</span>
      <button onClick={onDismiss} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export default function DlqMonitoring() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [page, setPage] = useState(1);
  const [notesModal, setNotesModal] = useState(null);

  const apiBase = `${import.meta.env.VITE_API_URL || ''}/api/v1/admin/dlq`;

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (filters.status) {
        params.set('status', filters.status);
      }
      if (filters.original_queue) {
        params.set('original_queue', filters.original_queue);
      }

      const response = await fetch(`${apiBase}?${params}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setEntries(data.data || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, page, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/stats`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setStats(data.data || null);
    } catch (_err) {
      // Stats are non-critical — silently ignore
    }
  }, [apiBase]);

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [fetchEntries, fetchStats]);

  const executeAction = async (entryId, action, notes) => {
    try {
      const body = notes ? { notes } : {};

      const response = await fetch(`${apiBase}/${entryId}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      fetchEntries();
      fetchStats();
    } catch (err) {
      setActionError(`Action failed: ${err.message}`);
    }
  };

  const handleAction = (entryId, action) => {
    if (action === 'resolve' || action === 'dismiss') {
      setNotesModal({ entryId, action });
    } else if (action === 'retry-escalated') {
      setNotesModal({ entryId, action: 'retry-escalated' });
    } else {
      executeAction(entryId, action);
    }
  };

  const handleNotesConfirm = (notes) => {
    if (notesModal) {
      if (notesModal.action === 'retry-escalated') {
        // Escalated retry uses the standard retry endpoint
        executeAction(notesModal.entryId, 'retry');
      } else {
        executeAction(notesModal.entryId, notesModal.action, notes);
      }
    }
    setNotesModal(null);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '24px' }}>DLQ Monitoring Dashboard</h1>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '4px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      <ErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      <StatsBanner stats={stats} />
      <FilterBar filters={filters} onFilterChange={(f) => { setFilters(f); setPage(1); }} />

      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center' }}>Loading...</div>
      ) : (
        <EntryList
          entries={entries}
          pagination={pagination}
          onPageChange={setPage}
          onAction={handleAction}
          onSelect={setSelectedEntry}
        />
      )}

      <EntryDetailModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />

      {notesModal && (
        <NotesModal
          action={notesModal.action}
          onConfirm={handleNotesConfirm}
          onCancel={() => setNotesModal(null)}
        />
      )}
    </div>
  );
}
